import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowLeft, ArrowRight, Share2 } from 'lucide-react';
import { supabase, hasSupabaseCredentials } from '../lib/supabaseClient';
import { toast } from 'sonner';
import { useDocumentMeta } from '../lib/useDocumentMeta';

interface BlogPost {
  id: number;
  title: string;
  content: string;
  author: string;
  published_at: string;
  tags: string[];
  slug: string;
  read_time: number;
  excerpt: string;
  status?: 'draft' | 'published' | 'archived';
  published?: boolean;
}

const fallbackContent: { [key: string]: BlogPost } = {
  'why-ai-is-a-skill-not-a-subject': {
    id: 1,
    title: 'Why AI Is a Skill, Not a Subject',
    content: `<h2>The difference between knowing about something and being able to do it</h2>
<p>You can read every book on tennis strategy, watch every match, and memorize every statistic. None of it makes you a tennis player. The only thing that makes you a tennis player is picking up a racket and hitting balls until your arm gives out. AI is the same.</p>
<p>Most AI education treats it like a subject — something you study, memorize, and pass a test on. Courses, certifications, video modules, reading lists. They teach you what AI can do. They do not teach you how to do it. The gap between those two things is where almost everyone gets stuck.</p>

<h2>Why knowledge decays and skills compound</h2>
<p>When you read about a prompting technique, you remember it for a few days. If you do not use it, it fades. When you practice a prompting technique on a real challenge and get feedback, it becomes a habit. Habits do not fade — they compound.</p>
<p>This is why someone who has done 30 daily challenges is dramatically better at using AI than someone who has completed a 10-hour course. The course-taker has knowledge that is already decaying. The daily practitioner has skills that are compounding.</p>

<h2>What practicing AI actually looks like</h2>
<p>It is not open-ended chat. It is a specific, bounded task with a clear goal and a way to know whether you succeeded. Write a prompt that does X. Evaluate whether the output is correct. Rewrite the prompt to fix what went wrong. That is one rep. Seven minutes. Done.</p>
<p>The key is that each challenge targets a specific skill — not generic "AI ability" but something concrete like verification, reasoning, context-setting, or critique. You do not get better at AI in general. You get better at ten specific things that together make up AI skill.</p>

<h2>Why feedback is the accelerator</h2>
<p>Practice without feedback is just repetition. You can write 100 prompts and not improve if you never find out which ones worked and why. AI grading changes this loop: you submit, you get a score, you get specific feedback on what was strong and what was weak, and you know exactly what to fix next time.</p>
<p>This is the same mechanism that makes a good coach valuable in any sport. The difference is that AI feedback is instant, consistent, and available every single day. You do not need to wait for a weekly session. You get the rep and the feedback in the same seven minutes.</p>

<h2>What this means for how you should learn AI</h2>
<p>Stop reading about AI. Start doing it. Pick one challenge, spend seven minutes, and see what happens. If the experience teaches you more than the last article you read, you have your answer about which approach works.</p>`,
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['AI Literacy', 'Learning', 'Practice'],
    slug: 'why-ai-is-a-skill-not-a-subject',
    read_time: 6,
    excerpt: 'You cannot learn AI by reading about it any more than you can learn tennis by watching a video. The gap between knowing about AI and being good at it is practice.',
    status: 'published',
    published: true,
  },
  'how-daily-challenges-beat-courses': {
    id: 2,
    title: 'How Daily Challenges Beat Courses for Learning AI',
    content: `<h2>The course illusion</h2>
<p>A course feels productive. You sit down, watch a module, answer a quiz, and move on. You feel like you are learning. But what you are actually doing is consuming content, and content consumption is not skill acquisition.</p>
<p>The evidence on this is clear across every domain: passive instruction produces knowledge that decays within days. Active practice produces skills that compound over weeks. AI is no different from public speaking, coding, or writing in this respect. You learn by doing, getting feedback, and doing again.</p>

<h2>Why seven minutes is enough</h2>
<p>A daily challenge takes about seven minutes. That is short enough that you will actually do it every day, and long enough to complete one full rep: read the task, write your response, get graded, read the feedback. The constraint is the feature.</p>
<p>Longer sessions — 30 minutes, an hour — feel more serious but happen less often. A seven-minute daily streak that you actually maintain beats a two-hour weekly session that you skip half the time. Consistency beats intensity for skill-building, and the research on this is unambiguous.</p>

<h2>What a challenge gives you that a course does not</h2>
<p>A course gives you information. A challenge gives you a rep. The difference is that a rep produces a specific, graded piece of work that you can measure improvement on. You wrote a prompt. It scored 62. Next week you wrote a similar prompt. It scored 78. That is visible, concrete progress — not a feeling of having learned something.</p>
<p>Challenges also target specific skills. A course on "AI for business" teaches you a little about everything. A challenge on verification teaches you verification — just that, deeply, with feedback. Over 30 days, you build ten skills one at a time instead of building none of them all at once.</p>

<h2>The compounding effect</h2>
<p>Day 1: you are bad at everything. Day 7: you are slightly less bad at three skills. Day 30: you are noticeably competent at five or six. The curve is not linear — it is the kind of curve where nothing seems to happen for two weeks and then something clicks. That click is the skills starting to reinforce each other.</p>
<p>This is why daily challenges work. Not because any single challenge is transformative, but because 30 of them, with feedback, in sequence, produce the kind of compounding that turns a beginner into someone who is genuinely good at using AI.</p>`,
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Learning', 'Practice', 'Challenges'],
    slug: 'how-daily-challenges-beat-courses',
    read_time: 7,
    excerpt: 'Courses teach you what AI can do. Daily challenges teach you how to do it. The difference matters because knowledge decays in days while skills compound over weeks.',
    status: 'published',
    published: true,
  },
  'ten-ai-skills-that-matter': {
    id: 3,
    title: 'The 10 AI Skills That Actually Matter in 2026',
    content: `<h2>Beyond prompt engineering</h2>
<p>Ask anyone what it means to be good at AI and they will say "prompt engineering." That is one skill. There are nine more, and most of them matter more than prompting. Here is the full list, in order of how much they determine whether you are actually useful with AI.</p>

<h2>1. Verification</h2>
<p>Can you tell when the AI is wrong? This is the most important skill and the one almost nobody practices. The model produces confident, fluent text. If you cannot catch when it is hallucinating, every other skill is built on sand.</p>

<h2>2. Critique</h2>
<p>Can you identify what is weak about an AI output and ask for something better? Most people accept the first response. Skilled users tear it apart and ask again. The gap in quality between the first and second response is usually enormous.</p>

<h2>3. Reasoning</h2>
<p>Can you use AI to pressure-test your thinking rather than just answer your questions? The best use of AI is not as an answer machine but as a sparring partner. Ask it to argue against your position. If it makes a good case, your position was weak.</p>

<h2>4. Context-setting</h2>
<p>Can you give the AI enough background to be useful without dumping your entire life story? This is the difference between a prompt that gets a generic answer and one that gets a specific, useful answer. It is not about clever phrasing — it is about knowing what context matters.</p>

<h2>5. Prompting</h2>
<p>Yes, prompting matters — but it is fifth on this list, not first. A good prompt with bad verification is dangerous. A mediocre prompt with good verification and critique is fine. Prompting is a skill; it is just not the only one.</p>

<h2>6. Research</h2>
<p>Can you use AI to find information efficiently and then check whether that information is real? Research is not just asking questions — it is asking, verifying, cross-referencing, and knowing when to stop trusting the model and start trusting other sources.</p>

<h2>7. Writing</h2>
<p>Can you use AI to improve your writing without letting it flatten your voice? The best writers use AI as an editor, not a ghostwriter. The skill is knowing what to accept, what to reject, and how to keep the output sounding like you.</p>

<h2>8. Automation</h2>
<p>Can you identify repetitive tasks that AI can handle and set up systems to do them? This is where AI moves from a tool you use to a tool that works for you. It requires understanding both the task and the model's limits.</p>

<h2>9. Creativity</h2>
<p>Can you use AI to generate ideas you would not have thought of, and then evaluate which ones are worth pursuing? Creativity with AI is not about the model being creative — it is about you being a good editor of its output.</p>

<h2>10. Tool use</h2>
<p>Can you choose the right tool for the job? ChatGPT, Claude, Gemini, local models — they have different strengths. Knowing which to use when is a skill, and it is one that changes as the tools evolve.</p>

<h2>Why most people practice the wrong ones</h2>
<p>Most people practice prompting because it is the most visible skill. They ignore verification and critique because those skills are uncomfortable — they require you to disagree with the machine, which feels rude. But those are the skills that separate someone who is productive with AI from someone who is dangerous with it.</p>`,
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Skills', 'AI Literacy', 'Practice'],
    slug: 'ten-ai-skills-that-matter',
    read_time: 9,
    excerpt: 'Prompt engineering gets the hype, but it is one of ten skills that determine whether you are actually good at using AI. Here is the full list.',
    status: 'published',
    published: true,
  },
  'how-ai-grading-works': {
    id: 4,
    title: 'How AI Grading Works and Why It Accelerates Learning',
    content: `<h2>The feedback problem</h2>
<p>Every skill improves with feedback. The faster the feedback, the faster the improvement. This is why coaching works in sports, why code review works in engineering, and why editing works in writing. The bottleneck has always been getting feedback fast enough and often enough.</p>
<p>AI changes this. When you submit a challenge response, an AI grader evaluates it against a rubric and returns a score plus specific feedback in seconds. You do not wait a week for a human reviewer. You get the rep and the feedback in the same seven-minute session.</p>

<h2>What the grader looks for</h2>
<p>Each challenge has a rubric — a set of criteria that define what a strong response looks like. The grader evaluates your submission against those criteria and assigns a score. The rubric is specific to the skill the challenge targets: a verification challenge is graded on whether you caught the errors, a reasoning challenge on whether you used AI to pressure-test your thinking, a prompting challenge on whether your prompt was specific enough.</p>
<p>The score is not a judgment of you. It is a measurement of where your skill is right now. A 62 is not "bad" — it is information. It tells you which skills are strong and which need work.</p>

<h2>Why instant feedback changes the learning curve</h2>
<p>Without feedback, you can practice for months and not improve. You are just repeating. With feedback, every rep produces a data point: this approach scored 58, this one scored 71, this one scored 84. You start to see patterns. You start to understand what works and what does not, not because someone told you in a video but because you experienced it.</p>
<p>This is the same mechanism that makes video games addictive and effective as learning tools. You act, you get immediate feedback, you adjust, you act again. The loop is tight. The difference is that the skill you are building is real-world AI competence, not aiming a virtual character.</p>

<h2>What the skill deltas tell you</h2>
<p>After each challenge, you see which of the ten skill areas moved and by how much. This is the most useful piece of data the grader produces. It tells you not just how you did on this challenge but which skills are growing and which are stagnant. Over weeks, you see patterns: your prompting is climbing steadily, your verification is stuck, your reasoning spikes when you do argument challenges but not otherwise.</p>
<p>That data lets you choose what to practice next. Instead of doing random challenges, you target the skills that need work. This is the difference between practice and deliberate practice — and deliberate practice is what produces real improvement.</p>`,
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Grading', 'Feedback', 'Learning'],
    slug: 'how-ai-grading-works',
    read_time: 8,
    excerpt: 'Getting feedback on your work is the single fastest way to improve at any skill. When the feedback comes from AI, it is instant, specific, and consistent.',
    status: 'published',
    published: true,
  },
  'bring-your-own-problem-real-work': {
    id: 5,
    title: 'Bring Your Own Problem: Turning Real Work Into Practice',
    content: `<h2>The problem with practice problems</h2>
<p>Practice problems are useful but artificial. You practice on a challenge someone else designed, about a topic you do not care about. You learn the skill, but the context is fake. When you go to use the skill on your real work, there is a translation gap.</p>
<p>Bring Your Own Problem closes that gap. You bring a real task — something you actually need to do — and the system turns it into a guided coaching session that teaches AI skills as you make progress on the task.</p>

<h2>How it works</h2>
<p>You describe what you are trying to get done. The system breaks it into steps, each one targeting a specific AI skill. You work through the steps with the AI, getting coached on technique as you go. At the end, you see exactly which skills you practiced and how you did.</p>
<p>The result is twofold: you make progress on your real task, and you practice AI skills in the context where you will actually use them. There is no translation gap because the practice context is the real context.</p>

<h2>What kinds of problems work</h2>
<p>Almost anything that involves using AI to get something done. Preparing a presentation. Drafting a difficult email. Researching a decision. Outlining a document. Brainstorming a project plan. If the task involves thinking, writing, or research, it works as a coaching session.</p>
<p>The problems that do not work are the ones that are too vague. "Be better at AI" is not a problem. "Prepare a Q4 strategy presentation for my team" is. The more specific you are, the more useful the coaching becomes.</p>

<h2>Why this is different from just using ChatGPT</h2>
<p>When you use ChatGPT directly, you are on your own. You write the prompt, you accept the output, you move on. There is no coaching, no skill identification, no feedback on technique. You might get the task done, but you do not get better at the skill.</p>
<p>Bring Your Own Problem sits between you and the model. It guides your prompting, points out when you could be more specific, suggests techniques, and at the end tells you which skills you practiced. You get the task done and you get better at the same time.</p>`,
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Practice', 'Coaching', 'Product'],
    slug: 'bring-your-own-problem-real-work',
    read_time: 8,
    excerpt: 'The best way to practice a skill is on something you actually need to do. Bring Your Own Problem takes your real task and turns it into guided AI coaching.',
    status: 'published',
    published: true,
  },
  'why-streaks-work-ai-skills': {
    id: 6,
    title: 'Why Streaks Work for AI Skill-Building',
    content: `<h2>The streak is not the point</h2>
<p>A streak is a tracking mechanism, not a learning mechanism. What actually builds skill is the daily rep — the seven minutes of practice. The streak just makes sure you do it. But that is enough, because the hardest part of skill-building is not the practice itself. It is showing up.</p>

<h2>What the research says</h2>
<p>The evidence on habit formation and skill acquisition is consistent across domains: short, daily practice sessions produce better results than long, infrequent ones. A musician who practices 15 minutes a day outperforms one who practices two hours once a week. A coder who writes a little every day improves faster than one who does marathon sessions on weekends.</p>
<p>AI skill-building follows the same pattern. The skills are small and specific — verification, critique, context-setting — and each one benefits from frequent, short reps rather than occasional deep dives.</p>

<h2>Why seven minutes is the sweet spot</h2>
<p>Short enough that you cannot talk yourself out of it. Long enough to complete one full rep: read the challenge, write your response, get graded, read the feedback. The constraint removes the decision fatigue of "how long should I practice today?" The answer is always the same: seven minutes, one challenge.</p>
<p>This is why streaks work. Not because the streak itself has magic power, but because it removes the friction of deciding whether to practice. The decision is already made. You just do it.</p>

<h2>What happens when you miss a day</h2>
<p>You miss a day. The streak resets. This is fine. The skill does not reset — only the counter does. The reps you already did are still in your skill score. The streak is motivation, not measurement. Your skill score is the measurement, and it does not decay when you skip a day.</p>
<p>The danger is not missing a day. The danger is letting one missed day become two, then a week, then a month. The streak counter exists to make that less likely by making the cost of skipping visible. But if it breaks, the right move is to start a new one the next day, not to quit.</p>

<h2>The compounding effect over weeks</h2>
<p>Day 1 feels like nothing. Day 7 feels like a small improvement. Day 14 feels like something is clicking. Day 30 feels like you are genuinely competent. The curve is not linear — it is the kind of curve where the first two weeks feel slow and then the improvement becomes obvious. That is what compounding looks like, and it is what daily streaks produce.</p>`,
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Streaks', 'Practice', 'Learning'],
    slug: 'why-streaks-work-ai-skills',
    read_time: 7,
    excerpt: 'A seven-minute daily streak is not a gimmick. It is the mechanism that turns a one-time effort into a compounding skill.',
    status: 'published',
    published: true,
  },
  'prompt-engineering-overrated': {
    id: 7,
    title: 'Prompt Engineering Is Overrated. Here Is What Actually Matters.',
    content: `<h2>The prompting obsession</h2>
<p>Every AI course, every blog post, every LinkedIn influencer talks about prompt engineering. "Write better prompts." "Use this framework." "Here are 50 prompt templates." The implication is that if you write the perfect prompt, you will get the perfect output.</p>
<p>This is mostly wrong. Prompting matters, but it is one skill out of ten, and it is not the most important one. The skills that determine whether you are actually useful with AI are the ones nobody talks about.</p>

<h2>What matters more than prompting</h2>
<p><strong>Verification.</strong> Can you tell when the output is wrong? A perfect prompt that produces a hallucinated answer is worse than a mediocre prompt that produces an answer you verify. If you cannot catch errors, prompting skill is irrelevant — you will confidently act on bad information.</p>
<p><strong>Critique.</strong> Can you look at the output and say "this is weak because..."? Most people accept the first response. Skilled users reject it, explain why, and ask again. The second response is almost always better than the first. This skill — the willingness to push back — matters more than any prompt template.</p>
<p><strong>Context-setting.</strong> Can you give the model enough background to be useful? This is not about clever phrasing. It is about knowing what information the model needs and providing it clearly. A well-set context with a simple prompt beats a cleverly engineered prompt with no context every time.</p>

<h2>Why prompting is overrated</h2>
<p>Because the model has gotten good enough that it can figure out what you mean even from a mediocre prompt. Two years ago, prompt engineering mattered more because the models were less capable. Today, the model does a lot of the interpretation for you. What it cannot do for you is verify its own output, critique its own reasoning, or decide whether its answer is actually useful for your situation.</p>
<p>Those skills — verification, critique, context-setting — are the ones that require human judgment. And they are the ones almost no one practices.</p>

<h2>When prompting does matter</h2>
<p>Prompting matters at the margins. When you need a very specific format, when you are doing complex multi-step reasoning, when you need the model to follow a structure — then prompt quality makes a real difference. But these are edge cases, not the default. For most everyday use, a clear, specific prompt with good context is enough. The skill is in the clarity and specificity, not in the engineering.</p>

<h2>What to practice instead</h2>
<p>If you want to get better at AI, spend less time studying prompt templates and more time practicing verification and critique. Take an AI output and find three things wrong with it. Take a response and rewrite your prompt to fix the specific weakness you identified. These skills compound. Prompt templates do not — they expire when the model updates.</p>`,
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Prompting', 'Skills', 'AI Literacy'],
    slug: 'prompt-engineering-overrated',
    read_time: 9,
    excerpt: 'Everyone talks about writing the perfect prompt. Almost no one talks about the skills that actually determine whether AI output is useful.',
    status: 'published',
    published: true,
  },
  'one-month-of-daily-practice': {
    id: 8,
    title: 'From Beginner to Confident: What One Month of Daily Practice Looks Like',
    content: `<h2>Week one: feeling incompetent</h2>
<p>The first week is the hardest. You do not know what good looks like. Your scores are low. The feedback tells you what you did wrong but you are not yet sure how to fix it. Every challenge feels like it takes longer than seven minutes because you are thinking hard about basics.</p>
<p>This is normal. The first week is not about improvement — it is about calibration. You are learning what the rubric expects, what the grader notices, and what a strong response looks like. Your scores will not move much this week. That is fine. The movement comes later.</p>

<h2>Week two: the first clicks</h2>
<p>Somewhere around day 10, something clicks. You start to notice patterns in the feedback. The same critique shows up across different challenges — "be more specific," "check the source," "argue the other side." You start to anticipate what the grader will say before you submit. That anticipation is the beginning of skill.</p>
<p>Your scores start to move. Not dramatically — a few points here and there. But the direction is clear. You are getting better at two or three skills, and you can feel it in how you approach the challenge. You are not just writing a response anymore. You are writing it with intent.</p>

<h2>Week three: the plateau</h2>
<p>Improvement slows. Your scores are higher than week one but not climbing as fast. This is the plateau, and it is where most people quit. The plateau is not a wall — it is the skills consolidating. You are not getting worse. You are getting stable. The gains from week two are becoming habits rather than conscious effort.</p>
<p>The right move during a plateau is to change what you practice. If you have been doing prompting challenges, switch to verification. If you have been doing reasoning, switch to critique. The plateau breaks when you stress a different skill. Your overall score keeps climbing even when any single skill is flat.</p>

<h2>Week four: the shift</h2>
<p>By the end of the fourth week, the change is visible. Not just in the score — in how you think about AI. You approach a challenge differently. You read the task, you think about which skills it targets, you write with awareness of what you are practicing. You look at an AI output in your real work and notice things you would not have noticed a month ago — a claim that needs checking, a framing that could be better, a gap in the context.</p>
<p>This is what one month of daily practice produces. Not mastery — that takes longer. But the shift from "I use AI" to "I am skilled at using AI" is real, and it happens in 30 days of seven-minute reps. The second month builds on it. The third month makes it permanent. But the first month is where the shift happens.</p>`,
    author: 'PromptGPT Team',
    published_at: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString(),
    tags: ['Practice', 'Beginners', 'Progress'],
    slug: 'one-month-of-daily-practice',
    read_time: 7,
    excerpt: 'What happens when you commit to one seven-minute AI challenge every day for 30 days? The skill score moves, but the bigger change is in how you think about AI.',
    status: 'published',
    published: true,
  },
};

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    if (slug) {
      loadPost();
    }
  }, [slug]);

  const loadPost = async () => {
    setLoading(true);

    const fallbackPost = fallbackContent[slug || ''];

    if (!hasSupabaseCredentials) {
      if (fallbackPost) {
        setPost(fallbackPost);
        setUsingFallback(true);
      }
      setLoading(false);
      return;
    }

    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 5000)
      );

      const fetchPromise = supabase
        .rpc('get_blog_post_by_slug', { p_slug: slug })
        .single();

      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as any;

      if (error || !data) {
        if (fallbackPost) {
          setPost(fallbackPost);
          setUsingFallback(true);
        } else {
          throw new Error('Post not found');
        }
      } else {
        setPost(data);
        setUsingFallback(false);

        if (data.tags?.length > 0) {
          const { data: related } = await supabase
            .rpc('get_related_blog_posts', { p_exclude_id: data.id, p_tags: data.tags });

          setRelatedPosts(related || []);
        }
      }
    } catch {
      if (fallbackPost) {
        setPost(fallbackPost);
        setUsingFallback(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  useDocumentMeta({
    title: post ? `${post.title} — PromptGPT Blog` : 'Article — PromptGPT Blog',
    description: post?.excerpt || 'Practical writing on AI skills, daily practice, and learning to use AI well.',
    canonical: `https://promptgpt.ai/blog/${slug || ''}`,
    type: 'article',
    image: post?.featured_image ? `https://promptgpt.ai${post.featured_image}` : undefined,
    jsonLd: post ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.excerpt,
      datePublished: post.published_at,
      author: { '@type': 'Organization', name: post.author || 'PromptGPT Team' },
      publisher: { '@type': 'Organization', name: 'PromptGPT', url: 'https://promptgpt.ai/' },
      mainEntityOfPage: `https://promptgpt.ai/blog/${post.slug}`,
      keywords: (post.tags || []).join(', '),
    } : undefined,
  });

  if (loading) {
    return (
      <div className="bg-warm-50 min-h-screen pt-24">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="inline-block h-8 w-8 rounded-full border-2 border-ink-100 border-t-accent-600 animate-spin" />
          <p className="text-ink-500 mt-4 text-sm">Loading article…</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-warm-50 min-h-screen pt-24">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-3xl text-ink-900 mb-3">Article not found</h1>
          <p className="text-ink-600 mb-6">The article you're looking for is no longer here.</p>
          <Link to="/blog" className="btn-secondary">
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-warm-50 min-h-screen">
      <article>
        {/* Header */}
        <header className="border-b border-ink-100 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-28 pb-14">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900 transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4" />
                All articles
              </Link>

              <div className="flex flex-wrap gap-2 mb-6">
                {post.tags?.map((tag) => (
                  <span key={tag} className="chip">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-ink-900 mb-6">
                {post.title}
              </h1>

              <p className="text-xl text-ink-600 leading-relaxed mb-8">
                {post.excerpt}
              </p>

              <div className="flex items-center justify-between flex-wrap gap-4 pt-6 border-t border-ink-100">
                <div className="flex items-center gap-5 text-sm text-ink-500">
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    {post.author}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {formatDate(post.published_at)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {post.read_time} min read
                  </span>
                </div>
                <button onClick={handleShare} className="btn-ghost text-sm">
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </motion.div>
          </div>
        </header>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="prose-blog"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-16 bg-white border border-ink-100 rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-4 bg-warm-50 border border-ink-100 rounded-full px-3 py-1.5">
              Try it
            </div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight text-ink-900 mb-3">
              Put these ideas to work.
            </h3>
            <p className="text-ink-600 max-w-lg mx-auto mb-6 leading-relaxed">
              Complete one full challenge free. Get graded by AI. See your skill score.
            </p>
            <Link to="/try" className="btn-primary">
              Try a challenge free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Related */}
          {relatedPosts.length > 0 && (
            <section className="mt-20 pt-12 border-t border-ink-100">
              <h2 className="font-display text-2xl tracking-tight text-ink-900 mb-8">
                Keep reading
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group block bg-white border border-ink-100 rounded-2xl p-6 hover:border-ink-200 hover:shadow-soft transition-all"
                  >
                    <h3 className="font-display text-lg leading-snug tracking-tight text-ink-900 mb-2 group-hover:text-accent-700 transition-colors">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-ink-600 leading-relaxed line-clamp-2 mb-4">
                      {relatedPost.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-xs text-ink-500">
                      <Clock className="w-3 h-3" />
                      {relatedPost.read_time} min read
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {usingFallback && (
            <p className="mt-12 text-xs text-ink-400 text-center">
              Featured content from our editorial archive.
            </p>
          )}
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
